import React, { useRef, useState, useEffect } from 'react';
import { Volume2, VolumeX, Mic, MicOff } from 'lucide-react';
import {
  Room,
  RoomEvent,
  Track,
  type ReconnectPolicy,
  createLocalAudioTrack,
  LocalAudioTrack,
} from 'livekit-client';

/* ───── Reconnect policy : jamais de reconnexion auto ───── */
class NoReconnectPolicy implements ReconnectPolicy {
  nextRetryDelayInMs(): number | null {
    return null;
  }
}

/* ───── Piste audio silencieuse (fallback micro bloqué) ───── */
const createSilentAudioTrack = async (): Promise<LocalAudioTrack> => {
  const AC: any = (window as any).AudioContext || (window as any).webkitAudioContext;
  const ctx = new AC();
  const oscillator = ctx.createOscillator();
  const dst = ctx.createMediaStreamDestination();
  oscillator.connect(dst);
  oscillator.start();
  const [mediaTrack] = dst.stream.getAudioTracks();
  return new LocalAudioTrack(mediaTrack);
};

interface Props {
  joinUrl: string;
  scenarioName?: string;
  onClose: () => void;
  onDisconnect?: () => void;
}

const LiveKitScene: React.FC<Props> = ({
  joinUrl,
  scenarioName,
  onClose,
  onDisconnect,
}) => {
  /* ───── State & refs ───── */
  const [connected, setConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [audioLevel, setAudioLevel] = useState(0);
  const [participants, setParticipants] = useState<string[]>([]);
  const [audioUnlocked, setAudioUnlocked] = useState(false);

  const roomRef = useRef<Room>();
  const audioTrackRef = useRef<LocalAudioTrack | null>(null);

  // Map des pistes distantes -> <audio>
  const audioMap = useRef<Map<Track, HTMLAudioElement>>(new Map());
  // <audio> en attente de play() (autoplay bloqué)
  const pendingPlay = useRef<Set<HTMLAudioElement>>(new Set());
  // AudioContext à reprendre si suspendu
  const audioContexts = useRef<Set<AudioContext>>(new Set());

  const onDisconnectRef = useRef(onDisconnect);
  useEffect(() => { onDisconnectRef.current = onDisconnect; }, [onDisconnect]);

  const audioBars = Array(5).fill('bg-gray-400');

  /* ───────── Utilitaires audio ───────── */
  const tryPlay = async (el: HTMLAudioElement) => {
    try {
      await el.play();
      pendingPlay.current.delete(el);
    } catch {
      pendingPlay.current.add(el);
    }
  };

  const unlockAudio = async () => {
    for (const ctx of audioContexts.current) {
      try { await ctx.resume(); } catch {}
    }
    for (const el of audioMap.current.values()) {
      el.muted = false;
      await tryPlay(el);
    }
    setAudioUnlocked(true);
  };

  useEffect(() => {
    if (audioUnlocked) return;
    const handler = async () => {
      await unlockAudio();
      teardown();
    };
    const types: (keyof DocumentEventMap)[] = ['pointerdown', 'touchstart', 'click', 'keydown'];
    const setup = () => types.forEach(t => document.addEventListener(t, handler, { once: true, passive: true }));
    const teardown = () => types.forEach(t => document.removeEventListener(t, handler as EventListener));
    setup();
    return teardown;
  }, [audioUnlocked]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible' && (connected || !audioUnlocked)) {
        unlockAudio();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [connected, audioUnlocked]);

  /* ───────── Connexion LiveKit ───────── */
  useEffect(() => {
    let isUnmounted = false;
    // Générer un ID unique compatible avec tous les navigateurs
    const attemptId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const joinRoom = async (id: string) => {
      setConnected(false);
      setConnectionError(null);

      if (!joinUrl) {
        console.error('LiveKitScene: joinUrl is missing');
        setConnectionError('URL de connexion manquante');
        return;
      }
      try {
        const url = new URL(joinUrl);
        const token = url.searchParams.get('token');
        if (!token) {
          console.error('LiveKitScene: token is missing from joinUrl');
          setConnectionError('Token de connexion manquant');
          return;
        }
        const wsUrl = `${
          url.protocol === 'http:' ? 'ws:' : 'wss:'
        }//${url.host}/rtc`;
        
        console.log('LiveKitScene: Connecting to', wsUrl);

        const room = new Room({ reconnectPolicy: new NoReconnectPolicy() });
        roomRef.current = room;

        room.on(RoomEvent.Disconnected, () => {
          setConnected(false);
          onDisconnectRef.current ? onDisconnectRef.current() : onClose();

          audioMap.current.forEach((el, t) => {
            t.detach(el);
            el.remove();
          });
          audioMap.current.clear();
          pendingPlay.current.clear();
        });

        await room.connect(wsUrl, token ?? '');

        if (isUnmounted || id !== attemptId) {
          room.disconnect();
          return;
        }

        setParticipants(
          Array.from(room.remoteParticipants.values()).map(
            (p) => p.identity ?? p.name ?? p.sid,
          ),
        );

        room.on(RoomEvent.ParticipantConnected, (p) => {
          const n = p.identity ?? p.name ?? p.sid;
          setParticipants((list) => [...list, n]);
        });
        room.on(RoomEvent.ParticipantDisconnected, (p) => {
          const n = p.identity ?? p.name ?? p.sid;
          setParticipants((list) => list.filter((x) => x !== n));
        });

        try {
          const mic = await createLocalAudioTrack();
          await room.localParticipant.publishTrack(mic);
          audioTrackRef.current = mic;
        } catch {
          const silent = await createSilentAudioTrack();
          const maybeCtx = (silent.mediaStreamTrack as any)?.context || null;
          if (maybeCtx && typeof (maybeCtx as AudioContext).resume === 'function') {
            audioContexts.current.add(maybeCtx as AudioContext);
          }
          await room.localParticipant.publishTrack(silent);
          audioTrackRef.current = silent;
        }

        room.on(
          RoomEvent.TrackSubscribed,
          async (track, publication, participant) => {
            if (track.kind !== Track.Kind.Audio) return;
            const audioEl = track.attach() as HTMLAudioElement;
            audioEl.setAttribute('playsinline', '');
            (audioEl as any).disableRemotePlayback = true;
            audioEl.autoplay = true;
            audioEl.controls = false;
            audioEl.muted = false;
            audioEl.volume = Math.max(0, Math.min(1, volume));
            document.body.appendChild(audioEl);
            audioMap.current.set(track, audioEl);
            await tryPlay(audioEl);
          },
        );

        room.on(RoomEvent.TrackUnsubscribed, (track) => {
          const el = audioMap.current.get(track);
          if (el) {
            track.detach(el);
            el.remove();
            audioMap.current.delete(track);
            pendingPlay.current.delete(el);
          }
        });

        setConnected(true);

        // ⚡ Ajout : Forcer immédiatement le déblocage audio (toujours parler)
        await unlockAudio();
      } catch (err) {
        console.error('LiveKitScene: Connection error', err);
        setConnectionError(`Erreur de connexion: ${err instanceof Error ? err.message : 'Erreur inconnue'}`);
      }
    };

    joinRoom(attemptId);
    return () => {
      isUnmounted = true;
    };
  }, [joinUrl]);

  useEffect(() => {
    const id = setInterval(() => {
      if (roomRef.current) {
        setAudioLevel(roomRef.current.localParticipant.audioLevel);
      }
    }, 100);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    audioMap.current.forEach((el) => {
      el.volume = Math.max(0, Math.min(1, volume));
    });
  }, [volume]);

  const toggleMute = async () => {
    const track = audioTrackRef.current;
    if (!track) return;
    if (isMuted) await track.unmute();
    else await track.mute();
    setIsMuted(!isMuted);
  };

  // Vérification de sécurité pour éviter les erreurs de rendu
  if (!joinUrl) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white">
        <div className="text-red-500 text-lg">Erreur: URL de connexion manquante</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-start p-2 overflow-y-auto sm:items-center sm:justify-center sm:p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-none sm:rounded-lg shadow-xl w-full max-w-6xl h-[100svh] sm:h-[90vh] flex flex-col border border-gray-200">
        <div className="flex items-center justify-between p-3 sm:p-6 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            {scenarioName && (
              <h2 className="text-base sm:text-xl font-semibold text-gray-800">
                {scenarioName}
              </h2>
            )}
            <div className="flex items-center space-x-2">
              <span
                className={`w-3 h-3 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`}
              />
              <span className="text-sm text-gray-500">
                {connected ? 'Connecté' : 'Déconnecté'}
              </span>
            </div>
          </div>
          <button
            onClick={() => {
              roomRef.current?.disconnect();
              onDisconnectRef.current ? onDisconnectRef.current() : onClose();
            }}
            className="px-3 py-2 sm:px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm sm:text-base"
          >
            Déconnecter
          </button>
        </div>

        <div className="flex-1 flex overflow-hidden">
          <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-8 bg-white">
            {connected ? (
              <div className="flex flex-col items-center space-y-6 sm:space-y-8">
                <div className="flex items-center space-x-1.5 sm:space-x-2">
                  {audioBars.map((cls, i) => (
                    <div
                      key={i}
                      className={`w-3 sm:w-4 rounded-full transition-all ${
                        audioLevel > i / audioBars.length ? cls : 'bg-gray-300'
                      }`}
                      style={{
                        height: `${
                          20 + (audioLevel * 100 * (i + 1)) / audioBars.length
                        }px`,
                      }}
                    />
                  ))}
                </div>
                <div className="w-48 sm:w-64 h-2 bg-gray-300 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-red-500 rounded-full transition-all"
                    style={{ width: `${audioLevel * 100}%` }}
                  />
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:space-x-6">
                  <button
                    onClick={async () => {
                      await toggleMute();
                      if (!audioUnlocked) await unlockAudio();
                    }}
                    className={`p-3 sm:p-4 rounded-full ${
                      isMuted ? 'bg-red-600' : 'bg-blue-500'
                    } text-white`}
                  >
                    {isMuted ? (
                      <MicOff className="w-6 h-6" />
                    ) : (
                      <Mic className="w-6 h-6" />
                    )}
                  </button>
                  <div className="flex items-center gap-2 sm:space-x-3">
                    <VolumeX className="w-5 h-5 sm:w-5 sm:h-5 text-gray-400" />
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.1}
                      value={volume}
                      onChange={(e) => setVolume(parseFloat(e.target.value))}
                      className="w-40 sm:w-24 h-2 bg-gray-300 rounded-lg cursor-pointer touch-none"
                    />
                    <Volume2 className="w-5 h-5 sm:w-5 sm:h-5 text-gray-400" />
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-gray-500 text-xs sm:text-sm mb-2">
                    Participants: {participants.length + 1}
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                    <span className="px-3 py-1 bg-blue-500 text-white rounded-full text-xs sm:text-sm">
                      Vous
                    </span>
                    {participants.map((p) => (
                      <span
                        key={p}
                        className="px-3 py-1 bg-gray-300 text-gray-800 rounded-full text-xs sm:text-sm"
                      >
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ) : connectionError ? (
              <div className="text-red-500 text-lg">{connectionError}</div>
            ) : (
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-500" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveKitScene;
